import { IAlertProps, IBaseAlertProps } from "@components/alert/Alert.types";
import { IProps as IIconProps } from "@components/icon";
import {
    enhanceFieldVisibility,
    getInfoValue,
    IFieldDef,
    IGetValueArgs,
    TGetValueFn,
    TInfoValue,
    TInfoValueProp
} from "@components/smart/FieldInfo";
import {
    IDependentFieldDef,
    IFormGroupDef,
    IFormLineItemsDef,
    TFormTable
} from "@components/smart/smartFormGroup/SmartFormGroup";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { isODataError, ODataError } from "@odata/Data.types";
import {
    correctEnumValue,
    getBoundValue,
    getNestedValue,
    isNavigationForUpdate,
    setBoundValue,
    setNestedValue
} from "@odata/Data.utils";
import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { getEnumDisplayValue } from "@odata/GeneratedEnums.utils";
import { getOldestActiveFY } from "@pages/fiscalYear/FiscalYear.utils";
import { IDefinition } from "@pages/PageUtils";
import { isNetworkError } from "@utils/customFetch";
import { getFocusableInputs, isNotDefined } from "@utils/general";
import { Dayjs } from "dayjs";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React from "react";

import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { BackendErrorCode, FormMode, NavigationSource, Status } from "../../enums";
import { TRecordAny, TValue } from "../../global.types";
import { IAdditionalData } from "../../model/Model";
import { IFilterQuery } from "../../model/TableStorage";
import BindingContext, { IEntity } from "../../odata/BindingContext";
import { getUtcDayjs } from "../../types/Date";
import { TableButtonsAction } from "../table/TableToolbar.utils";
import { ICustomHeaderAction, IFormDef } from "./Form";
import { FormStorage } from "./FormStorage";

export interface IEditOverride {
    id: string;
    icon: React.ComponentType<IIconProps>;
    description: string;
}

export enum FormType {
    Default = "Default",
    VirtualizedList = "VirtualizedList",
}

export const setBreadCrumbs = (storage: FormStorage): void => {
    storage.context.setViewBreadcrumbs({
        items: [
            {
                key: "selectedAccount",
                link: null,
                title: storage.data.definition.getItemBreadCrumbText(storage)
            }
        ],
        lockable: false
    });
};

interface IProcessDependentFieldsArgs {
    dataBc: BindingContext;
    rootBc?: BindingContext;
    reverse?: boolean;
    setValue?: (bindingContext: BindingContext, newValue: unknown, skipTempData?: boolean) => void;
}

export function processDependentFields(fields: IDependentFieldDef[], itemAdditionalData: IEntity, entity: IEntity, args: IProcessDependentFieldsArgs): void {
    const { dataBc, rootBc, reverse, setValue } = args;
    for (const field of fields) {
        const bc = field.navigateFrom === NavigationSource.Itself ? rootBc : field.navigateFrom === NavigationSource.Parent ? rootBc.getParent() : dataBc;
        const boundedBc = bc.navigate(field.to?.id ?? field.from.id);
        let value = field.value ?? reverse ? getBoundValue({
            bindingContext: boundedBc,
            data: entity,
            dataBindingContext: dataBc
        }) : getNestedValue(field.from.id, itemAdditionalData);

        if (typeof field.formatter === "function") {
            value = field.formatter(value, { item: itemAdditionalData });
        }

        if (isNotDefined(value) && !!field.ignoreNullValue) {
            continue;
        }

        if (value === undefined) {
            // use null instead of undefined,
            // otherwise we don't send the value in the request, and it won't get deleted from the entity on save
            value = null;
        } else if (typeof value === "object") {
            value = cloneDeep(value);
        }
        if (reverse) {
            setNestedValue(value, field.from.id, itemAdditionalData);
        } else if (setValue) {
            setValue(boundedBc, value, true);
        } else {
            const shouldNavigateBindingContext = isNavigationForUpdate(boundedBc, value);
            setBoundValue({
                bindingContext: shouldNavigateBindingContext ? boundedBc.navigate(boundedBc.getKeyPropertyName()) : boundedBc,
                data: entity,
                newValue: value,
                dataBindingContext: dataBc,
                preventCloning: true
            });
            correctEnumValue(boundedBc, value, { data: entity, dataBindingContext: bc });
        }
    }
}


export const getSummaryItems = (storage: FormStorage): ISummaryItem[] => {
    const def = storage?.data?.definition;

    if (storage?.formMode === FormMode.AuditTrail) {
        for (const key of ["NumberOurs", "Name"]) {
            if (storage.data.bindingContext.isValidNavigation(key)) {
                return [{
                    id: key,
                    label: getEnumDisplayValue(EntityTypeName.EntityType, storage.data.bindingContext.getEntityType().getName())
                }];
            }
        }
        return [];
    }

    return def?.summary ?? [];
};

export const getEntitySetFromDefinition = (definition: IDefinition, context: IAppContext, parentId?: string): string => {
    return getInfoValue(definition, "entitySet", {
        context: context,
        data: { parentId: parentId }
    });
};

/**
 * Sets group status as new / editing -> if it's set to creating, we expand the group, otherwise we keep the previous state
 * @param groupId
 * @param storage
 * @param isCreating
 * @param forceExpand
 */
export function setGroupStatus(groupId: string, storage: FormStorage, isCreating: boolean, forceExpand?: boolean): void {
    const groupStatus = storage.getGroupStatus(groupId);
    // expand the group when group status change to isCreating, otherwise we keep the previous state
    const isExpanded = forceExpand || isCreating || groupStatus?.isExpanded;

    storage.setGroupStatus({
        isCreating,
        isExpanded
    }, groupId);
}

export const getFormTableFilterQuery = (storage: FormStorage, tableDef: TFormTable, bindingContext: BindingContext): IFilterQuery => {
    const defaultFilter = getInfoValue(tableDef, "filter", {
        storage
    });

    return {
        query: (typeof defaultFilter === "string" ? defaultFilter : defaultFilter?.query) ?? `${tableDef.parentKey} eq ${bindingContext.getKey()}`,
        collectionQueries: defaultFilter?.collectionQueries
    };
};

export const isRequiredOnlyInTemporalDialog = (args: IGetValueArgs): boolean => {
    return !args.isValidateAll;
};

export function getAlertFromError(error: ODataError | Error | string): IBaseAlertProps {
    // use Set instead of Array, so that none of the messages is repeated multiple times
    const subTitle = new Set<string>();
    let detailData: TRecordAny;
    let title = i18next.t("Common:Errors.ErrorHappened");
    const internalError = i18next.t("Common:Errors.InternalError");

    if (isODataError(error)) {
        const isFieldValidationError = error._code === BackendErrorCode.ValidationError;

        if (isFieldValidationError) {
            title = i18next.t("Common:General.FormValidationErrorTitle");
        }

        detailData = error;

        if (error?._validationMessages?.length) {
            for (const valMessage of error._validationMessages) {
                subTitle.add(valMessage.message);
            }
        } else if (!!BackendErrorCode[error?._code]) {
            subTitle.add(error._message ?? internalError);
        } else {
            // unknown error, just show not translated code & message
            // show nothing in subtitle and display full error in detail
            subTitle.add(internalError);
        }
    } else if (isNetworkError(error)) {
        title = i18next.t("Common:Errors.NoConnection");
        subTitle.add(i18next.t("Common:Errors.NoConnectionMessage"));
    } else {
        if (typeof error === "string") {
            subTitle.add(error);
        } else {
            subTitle.add(internalError);
            detailData = { "Error": (error as unknown)?.toString() };
        }
    }

    return {
        detailData,
        status: Status.Error,
        useFade: false,
        title,
        subTitle: Array.from(subTitle)
    };
}

export function getDisabledFormPageAlert(): IAlertProps {
    return {
        status: Status.Warning,
        title: i18next.t("Common:Errors.PageLocked"),
        isSmall: true,
        useFade: true
    };
}

export function addLineActionInfoValueCallback(itemsGroup: IFormGroupDef, name: TInfoValueProp, actionIds: string[], callback: TGetValueFn<boolean>): void {
    const originalValue = itemsGroup.lineItems[name as keyof IFormLineItemsDef] as TInfoValue<boolean>;

    itemsGroup.lineItems = {
        ...itemsGroup.lineItems,
        [name]: (args: IGetValueArgs, smartFastEntryListProps: TRecordAny): boolean => {
            const customActionId = smartFastEntryListProps.customAction?.id;

            // possibility for multiple callbacks from different wrappers
            // (e.g. if assets wanted to have this check as well)
            if (!actionIds.includes(customActionId)) {
                return typeof originalValue === "boolean" ? originalValue : originalValue?.(args, smartFastEntryListProps);
            }

            return callback(args, smartFastEntryListProps);
        }
    };
}

/**
 * pick new Date from active FY, so there is a match with loaded accounts and the default
 * value of the form is valid. Use it when form contains accounts, so they are correctly
 * loaded or the date is not set, so user sees the validation in that case
 */
export function defaultDateFromActiveFiscalYear(args: IGetValueArgs): Dayjs {
    const activeFY = getOldestActiveFY(args.context);
    const now = getUtcDayjs();
    if (!activeFY) {
        return null;
    } else if (now.isBetween(activeFY.DateStart, activeFY.DateEnd, "day", "[]")) {
        return now;
    } else if (now.isBefore(activeFY.DateStart, "day")) {
        return getUtcDayjs(activeFY.DateStart);
    }
    return getUtcDayjs(activeFY.DateEnd);
}

export function isFormReadOnly(args: IGetValueArgs): boolean {
    return !!(args.storage as FormStorage).isReadOnly;
}

export function isFormDisabled(args: IGetValueArgs): boolean {
    return !!(args.storage as FormStorage).isDisabled;
}

export function focusFirstInputField(el: HTMLElement): void {
    const elements = Array.from(getFocusableInputs(el)) ?? [];
    const firstElement = elements[0];

    firstElement?.focus({
        // only works in Firefox so far :/
        // @ts-ignore
        focusVisible: true
    });
}

export const getCurrentLineItem = (storage: FormStorage, bc: BindingContext): TRecordAny => {
    const items = storage.data.entity.Items;
    return getBoundValue({
        bindingContext: bc,
        dataBindingContext: storage.data.bindingContext,
        data: items
    });
};

export function getOrigValue(storage: FormStorage, bc: BindingContext): TValue {
    return getBoundValue({
        bindingContext: bc,
        data: storage.data.origEntity,
        dataBindingContext: storage.data.bindingContext
    });
}

export function getEntityETag(storage: FormStorage): string {
    return storage.data?.metadata?.etag;
}

export function getEntityMemoKey(storage: FormStorage): string {
    return [getEntityETag(storage), storage.data?.entity?.Id].join("-");
}

export const getCommonPDFExportButtonDef = (): ICustomHeaderAction[] => {
    const commonDef: Pick<ICustomHeaderAction, "iconName" | "ignoreMasterDisabled" | "isVisible"> = {
        iconName: "ExportPdf",
        ignoreMasterDisabled: true,
        isVisible: (args) => !args.storage.data.bindingContext.isNew()
    };
    return [
        {
            id: TableButtonsAction.MassExport,
            label: i18next.t("Components:Table.ExportToPDF"),
            ...commonDef
        }, {
            id: TableButtonsAction.MassExportEn,
            label: i18next.t("Components:Table.ExportToPDFEnglish"),
            ...commonDef
        }
    ];
};

export function addFormDef(def: IDefinition, fieldId: string, fieldDef: Partial<IFieldDef>): void {
    if (!def.form.fieldDefinition) {
        def.form.fieldDefinition = {};
    }

    def.form.fieldDefinition[fieldId] = {
        ...(def.form.fieldDefinition[fieldId] ?? {}),
        ...fieldDef
    };
}

export function getFormGroup(def: IDefinition, groupId: string): IFormGroupDef {
    return def.form.groups?.find(g => g.id === groupId);
}

export function addFormAdditionalProperty(def: IDefinition, ...defs: IFieldDef[]): void {
    if (!def.form.additionalProperties) {
        def.form.additionalProperties = [];
    }
    def.form.additionalProperties.push(...defs);
}

export const PUT_AT_START = -1;

export function addFormGroup(def: IDefinition, group: IFormDef["groups"][0], afterGroup?: string | typeof PUT_AT_START): void {
    if (!def.form.groups) {
        def.form.groups = [];
    }

    if (!def.form.groups.find(g => g.id === group.id)) {
        let idx = afterGroup === PUT_AT_START ? 0 : null;
        if (afterGroup && afterGroup !== PUT_AT_START) {
            const srchIdx = def.form.groups.findIndex(g => g.id === afterGroup);
            if (srchIdx >= 0) {
                idx = srchIdx + 1;
            }
        }
        if (idx !== null && idx >= 0) {
            def.form.groups.splice(idx, 0, group);
        } else {
            def.form.groups.push(group);
        }
    }
}

export function hasNestedError(additionalFieldData: Record<string, IAdditionalData>, bc: BindingContext): boolean {
    const basePath = bc.getFullPath();
    return Object.entries(additionalFieldData ?? {}).some(
        ([path, data]) => path.startsWith(basePath + "/") && !!data?.error
    );
}

export function addRowsToGroup(def: IDefinition, groupId: string, rows: IFormGroupDef["rows"]): void {
    const group = getFormGroup(def, groupId);
    if (group) {
        if (!group.rows) {
            group.rows = [];
        }
        group.rows.push(...rows);
    }
}

export function enhanceGroupFieldsVisibility(def: IDefinition, groupId: string, isVisibleCondition: TInfoValue<boolean>, clearIfInvisible?: boolean): void {
    const group = getFormGroup(def, groupId);
    if (group) {
        for (const row of group.rows ?? []) {
            for (const fieldDef of row ?? []) {
                enhanceFieldVisibility(def, fieldDef.id, isVisibleCondition, clearIfInvisible);
            }
        }
    }
}

export function getGroupCollectionTitle(storage: FormStorage, group: IFormGroupDef, index: number, item: IEntity): string {
    if (typeof group.collectionItemTitle === "string") {
        return group.collectionItemTitle;
    }

    return group.collectionItemTitle?.(index, item, storage) ?? getInfoValue(group, "title", { storage });
}